import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Avatar,
    Rating,
} from '@mui/material';

import getResponseFields from './getResponseFields';
import useImageFetcher from './useImageFetcher';

import {
    TableCellRenderer,
    ImageCell,            
    ParagraphCell,         
    RatingCell,            
    ProductListCell,      
    FillInTheBlankCell,   
    TableRatingCell        
} from './TableCells';

const DetailsResponseTable = ({ data }) => {

    const fields = getResponseFields(data);

    const images = useImageFetcher(fields.Image);

    return (
        <TableContainer
            component={Paper}
            sx={{
                width: '100%',
                maxWidth: { xs: '80%', md: '768px' , lg:'992px' },
                overflowX: 'auto',
                '& .MuiTable-root': { minWidth: '768px' },
                mx: 'auto',
                mt: '48px',
                direction: 'rtl',
                '& .MuiTableCell-root': { padding: '16px', fontSize: '14px', minWidth: '240px' },
                '& .MuiTableHead-root .MuiTableCell-root': { backgroundColor: '#034f96', fontWeight: 'bold', fontSize: '18px' , color:'white' },
            }}
        >
            <Table>

                {/* ----------------------------- TableHead ----------------------------- */}
                <TableHead>
                    <TableRow>
                        {fields.showFullName && (
                            <>
                                <TableCellRenderer>نام</TableCellRenderer>
                                <TableCellRenderer>نام خانوادگی</TableCellRenderer>
                            </>
                        )}
                        {fields.showEmail         && <TableCellRenderer>ایمیل</TableCellRenderer>}
                        {fields.showAddress       && <TableCellRenderer>آدرس</TableCellRenderer>}
                        {fields.showPhone         && <TableCellRenderer>شماره تلفن</TableCellRenderer>}
                        {fields.showDatePicker    && <TableCellRenderer>تاریخ</TableCellRenderer>}
                        {fields.showAppointment   && <TableCellRenderer>ساعت ملاقات</TableCellRenderer>}
                        {fields.showShortText     && <TableCellRenderer>متن کوتاه</TableCellRenderer>}
                        {fields.showLongText      && <TableCellRenderer>متن بلند</TableCellRenderer>}
                        {fields.showDropDown      && <TableCellRenderer>انتخاب از لیست</TableCellRenderer>}
                        {fields.showSingleChoice  && <TableCellRenderer>انتخاب تکی</TableCellRenderer>}
                        {fields.showMultiChoice   && <TableCellRenderer>انتخاب چندگانه</TableCellRenderer>}
                        {fields.showNumber        && <TableCellRenderer>عدد</TableCellRenderer>}
                        {fields.showFileUpload    && <TableCellRenderer>فایل</TableCellRenderer>}
                        {fields.showTime          && <TableCellRenderer>زمان</TableCellRenderer>}
                        {fields.showSpinner       && <TableCellRenderer>شمارنده</TableCellRenderer>}
                        {fields.showTable         && <TableCellRenderer>نظرسنجی</TableCellRenderer>}
                        {fields.showStarRating    && <TableCellRenderer>امتیاز ستاره‌ای</TableCellRenderer>}
                        {fields.showScaleRating   && <TableCellRenderer>امتیاز مقیاسی</TableCellRenderer>}
                        {fields.showProductList   && <TableCellRenderer>لیست محصولات</TableCellRenderer>}
                        {fields.showImage         && <TableCellRenderer>تصویر</TableCellRenderer>}
                        {fields.showFillInTheBlank&& <TableCellRenderer>جای خالی را پر کنید</TableCellRenderer>}
                        {fields.showParagraph     && <TableCellRenderer>پاراگراف</TableCellRenderer>}
                    </TableRow>
                </TableHead>

                {/* ----------------------------- TableBody ----------------------------- */}
                <TableBody>
                    <TableRow>

                        {/* FullName */}
                        {fields.showFullName && (
                            <>
                                <TableCellRenderer>
                                    {fields.FullName?.state?.mainFirstNameInput || ''}
                                </TableCellRenderer>
                                <TableCellRenderer>
                                    {fields.FullName?.state?.mainLastNameInput || ''}
                                </TableCellRenderer>
                            </>
                        )}

                        {fields.showEmail      && <TableCellRenderer>{fields.Email?.state?.mainEmailInput || ''}</TableCellRenderer>}
                        {fields.showAddress    && <TableCellRenderer>
                            {`${fields.Address?.state?.mainStreetInput || ''}، ${fields.Address?.state?.mainCityInput || ''}، ${fields.Address?.state?.mainProvinceInput || ''}، ${fields.Address?.state?.mainPostalInput || ''}`}
                        </TableCellRenderer>}
                        {fields.showPhone      && <TableCellRenderer>{fields.Phone?.state?.mainPhoneInput || ''}</TableCellRenderer>}
                        {fields.showDatePicker && <TableCellRenderer>{fields.DatePicker?.state?.mainDate || ''}</TableCellRenderer>}

                        {fields.showAppointment && (
                            <TableCellRenderer>
                                {`${fields.Appointment?.state?.mainSavedSelectTime || ''} - ${fields.Appointment?.state?.mainPersianDateTime || ''}`}
                            </TableCellRenderer>
                        )}

                        {fields.showShortText && <TableCellRenderer>{fields.ShortText?.state?.mainShortTextInputValue || ''}</TableCellRenderer>}
                        {fields.showLongText  && <TableCellRenderer>{fields.LongText?.state?.mainTextareaValue       || ''}</TableCellRenderer>}

                        {fields.showDropDown     && <TableCellRenderer>{fields.DropDown?.state?.mainOption           || ''}</TableCellRenderer>}
                        {fields.showSingleChoice && <TableCellRenderer>{fields.SingleChoice?.state?.mainSelectedOption|| ''}</TableCellRenderer>}
                        {fields.showMultiChoice  && <TableCellRenderer>
                            {(fields.MultiChoice?.state?.mainSelectedTitles || []).join('، ')}
                        </TableCellRenderer>}

                        {fields.showNumber     && <TableCellRenderer>{fields.Number?.state?.mainNumberInputValue     || ''}</TableCellRenderer>}
                        {fields.showFileUpload && <TableCellRenderer>{fields.FileUpload?.state?.mainSelectedFileName  || ''}</TableCellRenderer>}
                        {fields.showTime       && <TableCellRenderer>
                            {`${fields.Time?.state?.mainInputValue || ''} ${fields.Time?.state?.selectedOption || ''}`}
                        </TableCellRenderer>}
                        {fields.showSpinner    && <TableCellRenderer>{fields.Spinner?.state?.mainSpinnerInputValue   || ''}</TableCellRenderer>}

                        {/* Table rating */}
                        {fields.showTable && <TableRatingCell tableData={fields.Table?.state} />}

                        {fields.showStarRating && <RatingCell rating={fields.StarRating?.state?.mainRate} />}

                        {fields.showScaleRating && (
                            <TableCellRenderer>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    <Typography fontWeight="bold">
                                        {(fields.ScaleRating?.state?.mainActive || 0) + '/5'}
                                    </Typography>
                                </Box>
                            </TableCellRenderer>
                        )}

                        {fields.showProductList && <ProductListCell productList={fields.ProductList?.state} />}

                        {fields.showImage && <ImageCell images={images} image={fields.Image} />}

                        {fields.showFillInTheBlank && <FillInTheBlankCell fillData={fields.FillInTheBlank} />}

                        {fields.showParagraph && <ParagraphCell paragraph={fields.Paragraph} />}

                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DetailsResponseTable;